import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate, useParams } from 'react-router-dom';
import {
  HEADER_H, LINE_H, MARGIN_X, ROWS, pendingRows, rowBandY, strokesForRows, uuidv7,
  type Page, type Paper, type Stroke, type TextBlock,
} from 'shared';
import { InkEngine } from '../ink/InkEngine.js';
import type { RowMarkKind } from '../ink/paper.js';
import { renderPageImage, renderThumbnail } from '../ink/pageImage.js';
import type { DebugStats, Viewport } from '../ink/types.js';
import {
  db, deleteStrokes, deleteTextBlock, loadStrokes, savePage, saveStroke, saveTextBlock, setMeta,
} from '../db/db.js';
import { syncer } from '../sync/sync.js';
import { api, ApiError } from '../api.js';
import { useApp } from '../state.js';
import TopBar, { SyncBadge } from '../ui/TopBar.js';
import ReviewPanel, { type ReviewItem } from './ReviewPanel.js';
import ToolBar, { activePen, loadToolState, saveToolState, type ToolState } from '../ui/ToolBar.js';

interface ServerItem {
  id: string; rows: number[]; kind: string; status: string; doubt: string | null;
  todoist_state: string | null;
}

export default function PageView(): JSX.Element {
  const { pageId = '' } = useParams();
  const navigate = useNavigate();
  const { settings, setSettings, debug, toggleDebug, showToast } = useApp();

  const hostRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<InkEngine | null>(null);
  const [tools, setTools] = useState<ToolState>(loadToolState);
  const [strokeCount, setStrokeCount] = useState(0);
  const [rowsPending, setRowsPending] = useState<number[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [stats, setStats] = useState<DebugStats | null>(null);
  const [sending, setSending] = useState(false);
  const [review, setReview] = useState<{ runId: string; items: ReviewItem[] } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [serverItems, setServerItems] = useState<ServerItem[]>([]);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>({ scale: 1, tx: 0, ty: 0 });
  const [editingBlock, setEditingBlock] = useState<string | null>(null);

  const page = useLiveQuery(() => db.pages.get(pageId), [pageId]);
  const textBlocks = useLiveQuery(
    async () => (await db.textBlocks.where('pageId').equals(pageId).toArray()).filter((b) => !b.deletedAt),
    [pageId], [] as TextBlock[],
  );
  const pens = settings?.pens ?? [];

  const refreshPending = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    const strokes = engine.getStrokes();
    setStrokeCount(strokes.length);
    setRowsPending(pendingRows(strokes));
  }, []);

  // Texto digitado conta como conteúdo novo igual ao traço: se só ele mudou,
  // o Enviar precisa oferecer aquela linha.
  const pendingAll = useMemo(() => {
    const set = new Set(rowsPending);
    for (const b of textBlocks) {
      if (!b.processedRunId && b.text.trim()) set.add(b.row);
    }
    return [...set].sort((a, b) => a - b);
  }, [rowsPending, textBlocks]);

  // ------------------------------------------------------------ motor

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !settings) return;

    // Montar o motor é o ponto em que o navegador do tablet pode recusar o
    // canvas. Sem este try, a exceção sobe, o React desmonta a tela e o que
    // sobra é o fundo escuro do body — indistinguível de um bug de desenho.
    let engine: InkEngine;
    try {
      engine = new InkEngine(
      host,
      {
        fingerDraw: settings.fingerDraw,
        palmRejectPx: settings.palmRejectPx,
        debug,
        lowLatencyInk: settings.lowLatencyInk !== false,
      },
      {
        onStrokeCommitted: (stroke) => {
          // Salvar fora do caminho crítico do desenho (SPEC §7.2): o traço já
          // está na tela, gravar pode esperar o próximo tempo ocioso.
          const withPage: Stroke = { ...stroke, pageId };
          queueMicrotask(() => {
            void saveStroke(withPage).then(() => syncer.schedule());
          });
          refreshPending();
        },
        onStrokesDeleted: (ids) => {
          queueMicrotask(() => { void deleteStrokes(ids).then(() => syncer.schedule()); });
          refreshPending();
        },
        onSelectionChange: setSelection,
        onViewportChange: setViewport,
      },
      );
    } catch (err) {
      setEngineError(err instanceof Error ? err.message : String(err));
      return;
    }
    setEngineError(null);
    engineRef.current = engine;
    setViewport(engine.getViewport());

    void (async () => {
      try {
        const strokes = await loadStrokes(pageId);
        engine.load(strokes.map((s) => ({ ...s, pageId })));
        refreshPending();
      } catch (err) {
        // Falhar ao ler o banco local não pode apagar a página: melhor abrir
        // vazia com aviso do que não abrir.
        setEngineError(`Não consegui abrir os traços desta página: ${String(err)}`);
      }
    })();

    return () => {
      // Miniatura ao sair da página, como a biblioteca espera (SPEC §8).
      try {
        const thumb = renderThumbnail(engine.getStrokes(), (page?.paper ?? 'ruled') as Paper);
        if (thumb) void setMeta(`thumb:${pageId}`, thumb);
      } catch { /* miniatura é enfeite: nunca pode impedir sair da página */ }
      engine.destroy();
      engineRef.current = null;
      void syncer.run();
    };
    // `page` de propósito fora das dependências: recriar o motor a cada
    // digitação no título perderia o estado de desfazer.
  }, [pageId, settings?.fingerDraw, settings?.palmRejectPx, settings?.lowLatencyInk]);

  useEffect(() => {
    engineRef.current?.setPaper((page?.paper ?? 'ruled') as Paper);
  }, [page?.paper]);

  // Uma fonte de verdade só para as ferramentas: o estado manda no motor e
  // é gravado, para a caneta voltar como estava na próxima página.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setMode(tools.mode);
    engine.setPen(activePen(tools));
    engine.setEraser(tools.eraser);
    engine.setSelectMode(tools.select);
    saveToolState(tools);
  }, [tools, engineError]);

  useEffect(() => {
    if (!debug) { setStats(null); return; }
    const id = window.setInterval(() => setStats(engineRef.current?.getStats() ?? null), 250);
    return () => window.clearInterval(id);
  }, [debug]);

  // Marcas da margem vêm do servidor (é lá que vive o resultado da revisão).
  const loadMarks = useCallback(async () => {
    try {
      const res = await api.get<{ items: ServerItem[] }>(`/api/pages/${pageId}/items`);
      setServerItems(res.items);
    } catch { /* offline: a página continua utilizável sem as marcas */ }
  }, [pageId]);

  useEffect(() => { void loadMarks(); }, [loadMarks]);

  const marks = useMemo(() => {
    const map = new Map<number, RowMarkKind>();
    for (const row of pendingAll) map.set(row, 'new');
    for (const item of serverItems) {
      for (const row of item.rows) {
        if (map.get(row) === 'new') continue; // linha mexida de novo: fica azul
        if (item.kind === 'task') map.set(row, 'task');
        else if (item.kind === 'event') map.set(row, 'event');
        else if (item.kind === 'ignore') map.set(row, 'ignored');
        else map.set(row, 'note');
      }
    }
    return map;
  }, [pendingAll, serverItems]);

  useEffect(() => { engineRef.current?.setMarks(marks); }, [marks]);

  // ---------------------------------------------------------- ferramentas

  /** Primeira linha livre depois do que já existe na página. */
  function nextFreeRow(): number {
    const used = new Set<number>();
    for (const st of engineRef.current?.getStrokes() ?? []) if (!st.deletedAt) used.add(st.row);
    for (const b of textBlocks) used.add(b.row);
    for (let r = 1; r <= ROWS; r++) if (!used.has(r)) return r;
    return ROWS;
  }

  async function addTextBlock(): Promise<void> {
    const row = nextFreeRow();
    const now = Date.now();
    const block: TextBlock = {
      id: uuidv7(),
      pageId,
      x: MARGIN_X + 12,
      y: rowBandY(row)[0] + 6,
      width: 780,
      text: '',
      row,
      createdAt: now,
      updatedAt: now,
    };
    await saveTextBlock(block);
    setEditingBlock(block.id);
    syncer.schedule();
  }

  async function updateTextBlock(block: TextBlock, text: string): Promise<void> {
    // Bloco vazio não deixa rastro: some quando perde o foco.
    if (!text.trim()) {
      await deleteTextBlock(block.id);
    } else {
      await saveTextBlock({ ...block, text, updatedAt: Date.now() });
    }
    syncer.schedule(1500);
    refreshPending();
  }

  async function updatePage(patch: Partial<Page>): Promise<void> {
    if (!page) return;
    await savePage({ ...page, ...patch, updatedAt: Date.now() });
    syncer.schedule(1500);
  }

  // -------------------------------------------------------------- enviar

  async function send(): Promise<void> {
    const engine = engineRef.current;
    if (!engine || !page || !pendingAll.length) return;
    setSending(true);
    try {
      const strokes = engine.getStrokes();
      const blob = await renderPageImage({
        strokes,
        rowsToProcess: pendingAll,
        paper: page.paper as Paper,
        textBlocks,
      });
      const toProcess = strokesForRows(strokes, pendingAll);

      // A leitura depende da rede por definição. Sem internet não entra numa
      // fila silenciosa: o app avisa e o Enviar continua disponível.
      const form = new FormData();
      form.append('image', blob, 'page.png');
      form.append('payload', JSON.stringify({
        pageId, rows: pendingAll, strokeIds: toProcess.map((s) => s.id), mode: 'items',
      }));

      await syncer.run(); // o servidor precisa conhecer os traços antes de lê-los
      const res = await api.form<{ runId: string; items: ReviewItem[]; suggestedTitle: string | null }>(
        '/api/ai/read-page', form,
      );
      setReview({ runId: res.runId, items: res.items });
      if (!page.title && res.suggestedTitle) await updatePage({ title: res.suggestedTitle });
    } catch (err) {
      const e = err as ApiError;
      showToast(
        !navigator.onLine
          ? 'Sem internet. A leitura precisa de rede — as linhas continuam marcadas como novas.'
          : e?.message ?? 'Não consegui ler esta página. Tentar de novo.',
      );
    } finally {
      setSending(false);
    }
  }

  async function transcribe(): Promise<void> {
    const engine = engineRef.current;
    if (!engine || !page) return;
    setMenuOpen(false);
    setSending(true);
    try {
      const strokes = engine.getStrokes();
      const rows = [...new Set([
        ...strokes.filter((s) => !s.deletedAt && s.row > 0).map((s) => s.row),
        ...textBlocks.filter((b) => b.text.trim()).map((b) => b.row),
      ])].sort((a, b) => a - b);
      if (!rows.length) return;
      const blob = await renderPageImage({ strokes, rowsToProcess: rows, paper: page.paper as Paper, textBlocks });
      const form = new FormData();
      form.append('image', blob, 'page.png');
      form.append('payload', JSON.stringify({ pageId, rows, strokeIds: [], mode: 'transcript' }));
      await syncer.run();
      await api.form('/api/ai/read-page', form);
      showToast('Página transcrita. Já aparece na busca.');
    } catch {
      showToast('Não consegui transcrever esta página.');
    } finally {
      setSending(false);
    }
  }

  const engine = engineRef.current;

  return (
    <>
      <TopBar title={
        <input
          className="title" value={page?.title ?? ''} placeholder="Título da página"
          onChange={(e) => void updatePage({ title: e.target.value })}
        />
      } back={page ? `/c/${page.notebookId}` : '/'}>
        <input
          type="date" style={{ width: 150 }} value={page?.pageDate ?? ''}
          onChange={(e) => void updatePage({ pageDate: e.target.value })}
        />
        <SyncBadge />
      </TopBar>

      <ToolBar
        state={tools}
        onChange={setTools}
        onEraseAll={() => { engine?.eraseAll(); refreshPending(); }}
        onKeyboard={() => void addTextBlock()}
        selectionCount={selection.length}
        onDeleteSelection={() => { engine?.deleteSelection(); refreshPending(); }}
        onRecolorSelection={(c) => engine?.recolorSelection(c)}
        onUndo={() => { engine?.undo(); refreshPending(); }}
        onRedo={() => { engine?.redo(); refreshPending(); }}
        onZoom={(f) => engine?.zoomBy(f)}
        onFit={() => engine?.fitWidth()}
        onMenu={() => setMenuOpen(true)}
        sendCount={pendingAll.length}
        sending={sending}
        onSend={() => void send()}
      />

      <div className="page-shell">
        <div className="canvas-host" ref={hostRef}>
          {engineError ? (
            <div className="engine-error">
              <strong>Não consegui abrir a área de escrita</strong>
              <p className="transcript" style={{ whiteSpace: 'pre-wrap' }}>{engineError}</p>
              <div className="row wrap">
                <button
                  className="primary"
                  onClick={() => {
                    void api.put('/api/settings', { lowLatencyInk: false }).then(() => {
                      if (settings) setSettings({ ...settings, lowLatencyInk: false });
                      location.reload();
                    });
                  }}
                >
                  Tentar sem baixa latência
                </button>
                <button onClick={() => navigate('/diag')}>Diagnóstico</button>
              </div>
            </div>
          ) : null}
          {/* O texto fica numa camada de HTML sobre o canvas: assim o teclado do
              Android, a seleção e o acento funcionam como o usuário espera, em vez
              de serem reimplementados dentro do canvas. */}
          {textBlocks.map((b) => (
            <div
              key={b.id}
              className="text-block"
              style={{
                left: viewport.tx + b.x * viewport.scale,
                top: viewport.ty + b.y * viewport.scale,
                width: b.width * viewport.scale,
                fontSize: 22 * viewport.scale,
                lineHeight: `${26 * viewport.scale}px`,
              }}
            >
              <textarea
                defaultValue={b.text}
                autoFocus={editingBlock === b.id}
                placeholder="Digite aqui…"
                rows={Math.max(1, b.text.split('\n').length)}
                onFocus={() => setEditingBlock(b.id)}
                onBlur={(e) => { setEditingBlock(null); void updateTextBlock(b, e.target.value); }}
              />
            </div>
          ))}

          {stats ? (
            <div className="hud">
              {`fps ${stats.fps}\nlatência ${stats.latencyMs} ms\nquadro ${stats.lastFrameMs.toFixed(1)} ms\ntraços ${stats.strokeCount}`}
            </div>
          ) : null}
        </div>

        {review ? (
          <ReviewPanel
            runId={review.runId}
            items={review.items}
            onFocusRows={(rows) => engine?.setHighlight(rows)}
            onDone={async (message) => {
              setReview(null);
              engine?.setHighlight([]);
              showToast(message);
              await syncer.fullPull();
              const strokes = await loadStrokes(pageId);
              engine?.load(strokes.map((s) => ({ ...s, pageId })));
              refreshPending();
              await loadMarks();
            }}
          />
        ) : null}
      </div>

      {menuOpen ? (
        <div className="sheet" onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}>
          <div>
            <h2 style={{ margin: 0 }}>Página</h2>
            <label className="stack small">
              Papel
              <select value={page?.paper ?? 'ruled'} onChange={(e) => void updatePage({ paper: e.target.value as Paper })}>
                <option value="ruled">Pautado</option>
                <option value="grid">Quadriculado</option>
                <option value="dotted">Pontilhado</option>
                <option value="blank">Liso</option>
              </select>
            </label>
            <label className="stack small">
              Projeto padrão desta página
              <input
                value={page?.defaultProject ?? ''} placeholder="Martori, Odontospecial…"
                onChange={(e) => void updatePage({ defaultProject: e.target.value || null })}
              />
            </label>
            {/* A barra de ferramentas fica FORA do canvas, então este menu
                continua alcançável mesmo com a área de escrita preta — é o
                único caminho de volta quando o driver do tablet entrega a
                camada de baixa latência opaca. */}
            <div className="field">
              <span className="label">A tela fica preta ao escrever?</span>
              <span className="hint">
                É o modo de baixa latência: em alguns tablets Android o driver devolve a
                camada da caneta opaca, e ela cobre a página. Desligar resolve na hora e
                custa um pouco de latência.
              </span>
              <button
                onClick={() => {
                  const next = !(settings?.lowLatencyInk !== false);
                  void api.put('/api/settings', { lowLatencyInk: next }).then(() => {
                    if (settings) setSettings({ ...settings, lowLatencyInk: next });
                    setMenuOpen(false);
                    showToast(next ? 'Baixa latência ligada.' : 'Baixa latência desligada. A tela deve voltar.');
                  });
                }}
              >
                {settings?.lowLatencyInk !== false ? 'Desligar baixa latência' : 'Ligar baixa latência'}
              </button>
            </div>
            <hr className="divider" />
            <div className="row wrap">
              <button onClick={() => void transcribe()}>Transcrever página inteira</button>
              <button onClick={() => { toggleDebug(); setMenuOpen(false); }}>
                {debug ? 'Esconder' : 'Mostrar'} medições
              </button>
              <button onClick={() => { navigate('/tasks'); }}>Ver tarefas</button>
            </div>
            <p className="muted small" style={{ margin: 0 }}>{strokeCount} traços nesta página.</p>
            <button onClick={() => setMenuOpen(false)}>Fechar</button>
          </div>
        </div>
      ) : null}
    </>
  );
}
